import React, { SetStateAction, useEffect } from 'react';
import {
    InputField,
    InputFieldProps,
    SelectField,
    SelectFieldOption,
    SelectFieldOptionGroup,
    TextField,
} from '../../atomic/forms';
import {
    FieldType,
    KintoneApp,
    KintoneField,
    TenantOptionsModel,
} from '../../../models/TenantModel';
import { Link, useParams } from 'react-router';
import { useTenant } from '../../../contexts/TenantContext';
import {
    getKintoneAppStatus,
    getTenantForKintoneApp,
    getTenantForKintoneAppFields,
    getTenantOptions,
    putTenantOptions,
} from '../../../APIs/tenants';
import { getAiAnalysisTypes } from '../../../APIs/templateApi';
import { Table, columnDefiner } from '../../atomic/table';
import { Button } from '../../atomic/buttons';
import { DialogBox } from '../../atomic/dialog';
import { Loading } from '../../atomic/effects';
import { Icons, Icon } from '../../atomic/icons';

interface AppData extends KintoneAppFiled {
    trClass: string;
}
const definer = columnDefiner<AppData>();

type Label = {
    aiAnalysisTypeId: number;
    aiName: string;
    aiNameJa: string;
    labelId: number;
    labelName: string;
    labelNameJa: string;
    color: string;
    runnable: boolean;
};

type KintoneAppFiled = {
    required: boolean;
    label: string;
    code: string;
    type: string;
    defaultValue: string;
};

type KintoneMessageProps = {
    appId: number;
    messages: Array<string>;
    setMessages: React.Dispatch<React.SetStateAction<string[]>>;
    setSystemMessages: React.Dispatch<React.SetStateAction<string[]>>;
};

const KintoneMessage: React.FC<KintoneMessageProps> = ({
    appId,
    messages,
    setMessages,
    setSystemMessages,
}) => {
    React.useEffect(() => {
        getKintoneAppStatus(Number(appId))
            .then((resp) => {
                if (resp.success) {
                    if (resp.messages) {
                        setSystemMessages(resp.messages);
                        setMessages(resp.messages);
                    }
                }
            })
            .catch((err) => {
                console.warn(err);
            });
    }, [appId]);
    if (!messages.length) {
        return <></>;
    }
    return (
        <div className={'messages'}>
            <div className={'error'}>
                {messages.map((message, index) => {
                    return <p key={`error-msgs-${index}`}>{message}</p>;
                })}
            </div>
        </div>
    );
};

export const AdminKintoneEdit: React.FC = () => {
    const [messages, setMessages] = React.useState<Array<string>>([]);
    const [systemMessages, setSystemMessages] = React.useState<Array<string>>(
        []
    );
    const [isLoading, setIsLoading] = React.useState(true);
    const [showConfirm, setShowConfirm] = React.useState(false);

    const { tenantId } = useTenant();

    const { appId } = useParams();
    const [app, setApLocal] = React.useState<KintoneApp>();
    const [kintoneOptions, setKintoneOptions] =
        React.useState<TenantOptionsModel>({});

    const [appList, setAppList] = React.useState<Array<SelectFieldOption>>([]);
    const [selectedAppId, setSelectedAppId] = React.useState<string>('');
    const [showAppIdCopyConfirm, setAppIdCopyShowConfirm] =
        React.useState(false);

    const [labels, setLabels] = React.useState<Label[]>([]);
    const [fields, setFields] = React.useState<KintoneAppFiled[]>([]);

    const [validations, setValidations] = React.useState<boolean>(false);

    const [selectOption, setSelectOption] = React.useState<
        Array<SelectFieldOptionGroup | SelectFieldOption>
    >([]);

    interface InputRef {
        appDescription: React.MutableRefObject<InputField<InputFieldProps>>;
    }
    const refs: InputRef = ['appDescription'].reduce((ret, key) => {
        ret[key] = React.useRef<InputField>();
        return ret;
    }, {} as InputRef);

    const setApp = (_app) => {
        setApLocal(_app);
        const _kintoneOptions = { ...kintoneOptions };
        if (!kintoneOptions.kintone) {
            return;
        }

        if (_kintoneOptions.kintone) {
            const _apps = [_app];
            _kintoneOptions.kintone.apps = _apps;
            setKintoneOptions(_kintoneOptions);
        }
    };

    const setAppDescription = (val: string) => {
        const _app = app;
        if (_app) {
            _app.appDescription = val;
            setApp(_app);
        }
    };

    const updateLabels = (types: any[]) => {
        const _labels: Array<Label> = [];
        const _selectOption: Array<SelectFieldOptionGroup | SelectFieldOption> =
            [
                {
                    text: '',
                    value: '',
                },
            ];
        _selectOption.push({
            label: 'ファイル',
            options: [
                {
                    text: '緯度',
                    value: 'LOCATION_LATITUDE',
                },
                {
                    text: '経度',
                    value: 'LOCATION_LONGITUDE',
                },
            ],
        });
        types
            .filter((type) => type.runnable)
            .map((type) => {
                const group = {
                    label: type.nameJa,
                    options: [
                        {
                            text: `URL(${type.nameJa})`,
                            value: `REELURL_${type.aiAnalysisTypeId}`,
                        },
                    ],
                };
                type.labels.forEach((label) => {
                    const data: Label = {
                        aiAnalysisTypeId: type.aiAnalysisTypeId,
                        aiName: type.name,
                        aiNameJa: type.nameJa,
                        labelId: label.id,
                        labelName: label.name,
                        labelNameJa: label.nameJa,
                        color: label.color,
                        runnable: type.runnable,
                    };
                    _labels.push(data);

                    group.options.push({
                        text: label.nameJa,
                        value: `${type.aiAnalysisTypeId}_${label.id}`,
                    });
                });
                _selectOption.push(group);
            });
        setLabels(_labels);
        setSelectOption(_selectOption);
    };

    const setErrorMessages = React.useCallback(
        (msg: string[]) => {
            setMessages([...systemMessages, ...msg]);
        },
        [systemMessages]
    );

    React.useEffect(() => {
        if (!kintoneOptions.kintone || !fields) {
            return;
        }
        const required = fields.some((field) => {
            // 必須ではない OR　デフォルト値が設定されていたらヴァリデーションから外す
            if (field.required == false || field.defaultValue.length > 0) {
                return false;
            }
            return kintoneOptions.kintone?.apps?.[0].fields
                ?.filter((appField) => field.code === appField.kintoneFieldName)
                ?.some((appField) => {
                    return appField.aiAnalysisLabelId == undefined;
                });
        });
        setValidations(required);
        if (required) {
            setErrorMessages(['空欄の必須項目があります']);
        } else {
            setErrorMessages([]);
        }
    }, [kintoneOptions, fields]);

    React.useEffect(() => {
        getAiAnalysisTypes()
            .then((resp) => {
                updateLabels(resp.data);
            })
            .catch((err) => {
                console.warn(err);
            });
    }, []);
    React.useEffect(() => {
        getTenantForKintoneAppFields(tenantId, Number(appId))
            .then((resp) => {
                if (resp.success) {
                    const _fields: KintoneAppFiled[] = resp.data.map(
                        (filed: any) => {
                            return filed;
                        }
                    );
                    setFields(_fields);
                } else {
                    console.warn(resp);
                }
            })
            .catch((err) => {
                console.warn(err);
            });
    }, [tenantId, appId]);

    useEffect(() => {
        if (tenantId) {
            const keyName = `apps_${appId}`;
            getTenantOptions({
                tenantId: tenantId,
                category: 'kintone',
                keys: [keyName],
                //array: ['apps'],
            })
                .then((resp) => {
                    if (resp.data) {
                        if (
                            resp.data.hasOwnProperty('kintone') &&
                            resp.data.kintone &&
                            resp.data.kintone.hasOwnProperty(keyName)
                        ) {
                            const _apps = [resp.data.kintone[keyName]];
                            const _option = {
                                kintone: {
                                    apps: _apps,
                                },
                            };
                            setKintoneOptions(_option);

                            const _app = resp.data.kintone[keyName];
                            if (_app) {
                                setApp(_app);
                            }
                        }
                    }
                })
                .catch((err) => {
                    console.warn(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });

            getTenantOptions({
                tenantId: tenantId,
                category: 'kintone',
                array: ['apps'],
            })
                .then((resp) => {
                    if (resp.data) {
                        if (
                            resp.data.hasOwnProperty('kintone') &&
                            resp.data.kintone &&
                            resp.data.kintone.apps &&
                            resp.data.kintone.hasOwnProperty('apps')
                        ) {
                            const _apps: SetStateAction<SelectFieldOption[]> =
                                resp.data.kintone.apps
                                    .filter((_app) => {
                                        return (
                                            Number(_app.appId) !== Number(appId)
                                        );
                                    })
                                    .map((_app) => {
                                        return {
                                            text: `${_app.appId}:${
                                                _app.appDescription ?? ''
                                            }`,
                                            value: _app.appId.toString(),
                                        };
                                    });
                            setAppList(_apps);
                        }
                    }
                })
                .catch((err) => {
                    console.warn(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [tenantId, appId]);

    useEffect(() => {
        if (app && app.appDescription === null) {
            // デフォルトの詳細を設定
            getTenantForKintoneApp(tenantId, Number(appId))
                .then((resp) => {
                    if (resp.success) {
                        const _app = { ...app };
                        _app.appDescription = resp.data.name;
                        setApp(_app);
                    } else {
                        console.warn(resp);
                    }
                })
                .catch((err) => {
                    console.warn(err);
                });
        }
    }, [app, tenantId]);

    const inputApp = (kintoneAppFiled: KintoneAppFiled, value: string) => {
        const code = kintoneAppFiled.code;
        if (app === undefined) {
            return;
        }
        const _app: KintoneApp = { ...app };
        const optionValues = getOptionValues(value, kintoneAppFiled);

        if (!_app.fields) {
            return;
        }

        const isSome = _app.fields.some((field) => {
            return field.kintoneFieldName === code;
        });
        if (isSome) {
            const updateFields = _app.fields.map((field) => {
                if (field.kintoneFieldName === code) {
                    field.aiAnalysisLabelId = optionValues.labelId;
                    field.type = optionValues.type;
                    field.aiAnalysisTypeId = optionValues.aiAnalysisTypeId;
                }
                return field;
            });
            _app.fields = updateFields;
        } else {
            _app.fields.push({
                kintoneFieldName: code,
                aiAnalysisLabelId: optionValues.labelId,
                type: optionValues.type,
                aiAnalysisTypeId: optionValues.aiAnalysisTypeId,
            });
        }
        setApp(_app);
    };

    const getKintoneFieldToOptionValue = (field: KintoneField) => {
        return `${field.aiAnalysisTypeId}_${field.aiAnalysisLabelId}`;
    };

    const getDefaultOptionValue = React.useCallback(
        (kintone: KintoneAppFiled) => {
            if (!app?.fields) {
                return '';
            }
            const field = app?.fields.find((field) => {
                if (!field) {
                    return false;
                }
                return field.kintoneFieldName === kintone.code;
            });
            if (!field) {
                return '';
            }

            return getKintoneFieldToOptionValue(field);
        },
        [app?.fields]
    );

    const getSelectedItemByCode = React.useCallback(
        (code: string) => {
            if (!app?.fields) {
                return undefined;
            }
            const fields = app.fields;

            const field = fields.find((field) => {
                if (!field) {
                    return false;
                }
                return field.kintoneFieldName === code;
            });
            return field;
        },
        [app]
    );

    const getSelectedAiTypeByCode = React.useCallback(
        (code: string) => {
            const field = getSelectedItemByCode(code);
            if (field?.type !== 'LABEL') {
                return '';
            }
            return field?.aiAnalysisTypeId;
        },
        [getSelectedItemByCode]
    );

    const getSelectedAiLabelIdByCode = React.useCallback(
        (code: string) => {
            const field = getSelectedItemByCode(code);
            if (field?.type !== 'LABEL') {
                return '';
            }
            return field?.aiAnalysisLabelId;
        },
        [getSelectedItemByCode]
    );

    const options = {
        labels,
        fields,
        app,
        inputApp,
        getDefaultOptionValue,
        getSelectedAiTypeByCode,
    };

    type OptionValues = {
        code: string;
        type: FieldType;
        aiAnalysisTypeId: string;
        labelId: string;
    };

    const getOptionValues = React.useCallback(
        (value: string, kintoneAppFiled: KintoneAppFiled) => {
            const result: string[] = value.split('_');

            const getType = (): FieldType => {
                switch (kintoneAppFiled.type) {
                    case 'LINK':
                        return 'REELURL';
                    case 'NUMBER':
                        return 'NUMBER';
                    default:
                        return 'LABEL';
                }
            };
            const type = getType();

            const optionValues = (): OptionValues => {
                return {
                    code: kintoneAppFiled.code,
                    type: type,
                    aiAnalysisTypeId: result[0],
                    labelId: result[1],
                };
            };
            return optionValues();
        },
        [app]
    );

    const columns = [
        {
            header: 'Kintoneラベル名',
            cell: (d: { row: { original: KintoneAppFiled } }) => {
                return (
                    <div className="label">
                        <span>{d.row.original.label}</span>
                        {d.row.original.required &&
                            d.row.original.defaultValue.length == 0 && (
                                <div className="color-error-text">{'必須'}</div>
                            )}
                    </div>
                );
            },
        },
        {
            header: 'フィールド名',
            cell: (d: { row: { original: KintoneAppFiled } }) => {
                return <div className="label">{d.row.original.code}</div>;
            },
        },
        {
            header: 'フィールド種別',
            cell: (d: { row: { original: KintoneAppFiled } }) => {
                const exp = d.row.original.hasOwnProperty('expression');
                return (
                    <div className="label">
                        <span>{d.row.original.type}</span>
                        {exp && (
                            <div className="color-error-text">{'自動計算'}</div>
                        )}
                    </div>
                );
            },
        },
        {
            header: '対象AIラベル',
            cell: (d: { row: { original: KintoneAppFiled } }) => {
                return (
                    <>
                        <SelectField
                            options={selectOption}
                            _onChange={(value) => {
                                inputApp(d.row.original, value);
                            }}
                            value={getDefaultOptionValue(d.row.original)}
                            required={d.row.original.required}
                        />
                    </>
                );
            },
        },
    ];

    const openConfirmDialog = React.useCallback(() => {
        let ok = true;
        for (let key in refs) {
            if (!refs[key].current?.checkValue()) {
                ok = false;
            }
        }
        if (ok) {
            setShowConfirm(true);
        }
    }, []);

    const onConfirmOK = React.useCallback(
        (dialog: DialogBox) => {
            setIsLoading(true);
            putTenantOptions(tenantId, kintoneOptions)
                .then((resp) => {
                    if (resp.status === 200) {
                        setShowConfirm(false);
                    } else {
                        console.warn(resp);
                        dialog.addError(
                            resp.message ||
                                'kintoneアプリ連携情報の更新に失敗しました'
                        );
                    }
                })
                .catch((err) => {
                    console.warn(err);
                    dialog.addError(
                        err.response?.data?.message ||
                            err.message ||
                            'kintoneアプリ連携情報の更新に失敗しました'
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [app, kintoneOptions]
    );

    const onConfirmCancel = () => {
        setShowConfirm(false);
    };

    const openConfirmAppCopyDialog = () => {
        if (selectedAppId) {
            setAppIdCopyShowConfirm(true);
        }
    };

    const appCopy = (_orgnalApp) => {
        if (!_orgnalApp) {
            setAppIdCopyShowConfirm(false);
            return;
        }

        // コピー元からアプリのフィールドのものだけ抽出
        const tpmCopyFields = fields.map((field) => {
            return _orgnalApp?.fields.find((_appField) => {
                return _appField.kintoneFieldName === field.code;
            });
        });
        const copyFields = tpmCopyFields.filter((tpmCopyField) => tpmCopyField);
        // コピー元にない設定は現在の設定を引き継ぎ
        const _app = { ...app };
        _app.fields?.map((field) => {
            if (
                copyFields.some((_copyField) => {
                    return (
                        _copyField?.kintoneFieldName === field.kintoneFieldName
                    );
                }) === false
            ) {
                copyFields.push(field);
            }
        });
        _app.fields = copyFields as KintoneField[];
        setApp(_app);
    };

    const showAppIdCopyConfirmOk = React.useCallback(() => {
        // コピー元の設定を取得
        setIsLoading(true);
        const keyName = `apps_${selectedAppId}`;
        getTenantOptions({
            tenantId: tenantId,
            category: 'kintone',
            keys: [keyName],
        })
            .then((resp) => {
                if (resp.data) {
                    if (
                        resp.data.hasOwnProperty('kintone') &&
                        resp.data.kintone &&
                        resp.data.kintone.hasOwnProperty(keyName)
                    ) {
                        appCopy(resp.data.kintone[keyName]);
                    }
                }
            })
            .catch((err) => {
                console.warn(err);
            })
            .finally(() => {
                setAppIdCopyShowConfirm(false);
                setShowCopyBlock(false);
                setIsLoading(false);
            });
    }, [fields, selectedAppId]);

    const showAppIdCopyConfirmCancel = () => {
        setAppIdCopyShowConfirm(false);
    };

    // 設定コピーダイアログ
    const [showCopyBlock, setShowCopyBlock] = React.useState(false);
    const openCopyDialog = () => {
        setShowCopyBlock(true);
    };
    const closeCopyDialog = () => {
        setShowCopyBlock(false);
    };
    return (
        <div className="app-main admin-kintone">
            {fields && app && (
                <>
                    <div className="edit-forms">
                        <div className="form">
                            <h2>Kintone連携パラメータの管理</h2>
                            <div>アプリID:{app.appId}</div>
                            <KintoneMessage
                                appId={Number(app.appId)}
                                messages={messages}
                                setMessages={setMessages}
                                setSystemMessages={setSystemMessages}
                            />
                            <div>
                                <Link onClick={openCopyDialog} to={'#'}>
                                    <Icon icon={Icons.Copy} />{' '}
                                    他のアプリから設定をコピーする
                                </Link>
                            </div>
                            <div>
                                <TextField
                                    label={'詳細'}
                                    value={app.appDescription}
                                    _onChange={setAppDescription}
                                    placeholder={app.appDescription}
                                    required={true}
                                    ref={refs.appDescription}
                                />
                            </div>
                            <Table
                                className="field-table"
                                data={fields}
                                columns={columns}
                                options={options}
                            />
                            <div className="update-buttons">
                                <Button
                                    className="primary"
                                    onClick={openConfirmDialog}
                                    disabled={validations}
                                >
                                    保存
                                </Button>
                            </div>
                            <Link to="/app/admin/kintone/apps">
                                kintoneアプリ設定へ戻る
                            </Link>
                        </div>
                    </div>
                </>
            )}
            {showConfirm && (
                <DialogBox
                    title="確認"
                    onOK={onConfirmOK}
                    onCancel={onConfirmCancel}
                >
                    Kintone連携の設定を変更しますか？
                </DialogBox>
            )}
            {showCopyBlock && (
                <DialogBox
                    title="他のアプリから設定をコピーする"
                    onOK={openConfirmAppCopyDialog}
                    onCancel={closeCopyDialog}
                    disabled={selectedAppId == ''}
                >
                    <SelectField
                        label={'コピー元を選択'}
                        options={appList}
                        _onChange={setSelectedAppId}
                        value={selectedAppId}
                    />
                </DialogBox>
            )}
            {showAppIdCopyConfirm && (
                <DialogBox
                    title="確認"
                    onOK={showAppIdCopyConfirmOk}
                    onCancel={showAppIdCopyConfirmCancel}
                >
                    Kintoneアプリ"{selectedAppId}"をロードしますか？
                </DialogBox>
            )}
            {isLoading && <Loading />}
        </div>
    );
};
